import { css, cx } from '@emotion/css';
import { useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { Clock } from 'lucide-react';
import { useGetOrderSession } from '../../hooks/checkout-api.hooks';

// Styles (Emotion CSS)
const container = css({
  width: '100%',
});

const progressBar = css({
  width: '100%',
  height: 4,
  backgroundColor: 'rgba(236, 236, 236, 0.8)',
});

const progressBarFill = css({
  height: '100%',
  backgroundColor: '#00CA84',
  transition: 'width 1s linear',
  boxShadow: '0 0 8px rgba(0, 202, 132, 0.5)',
});

const progressBarFillExpiring = css({
  backgroundColor: '#e74c3c',
  boxShadow: '0 0 8px rgba(231, 76, 60, 0.7)',
  transition: 'width 0.5s linear',
});

const timerWrapper = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: 'transparent',
});

const timer = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '6px 14px',
  backgroundColor: '#121212',
  color: 'white',
  fontWeight: 500,
  fontSize: 14,
  borderRadius: '0 0 8px 8px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderTop: 'none',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
  minWidth: 120,
});

const timerExpiring = css({
  backgroundColor: '#e74c3c',
  animation: 'sessionTimerPulse 1.5s infinite',
  '@keyframes sessionTimerPulse': {
    '0%': { backgroundColor: '#e74c3c' },
    '50%': { backgroundColor: '#c0392b' },
    '100%': { backgroundColor: '#e74c3c' },
  },
});

const timerText = css({
  fontFamily: 'JetBrains Mono, monospace',
  fontWeight: 600,
  letterSpacing: 0.5,
});

export interface SessionTimerProps extends HTMLAttributes<HTMLDivElement> {
  sessionId: string;
  onExpired?: () => void;
}

export function SessionTimer({ sessionId, onExpired, className, ...rest }: SessionTimerProps) {
  const { data } = useGetOrderSession(sessionId, !!sessionId);

  const expiresAtSeconds = useMemo(() => {
    const expiresAt = data?.session?.expiresAt;
    if (!expiresAt) return null;
    const t = new Date(expiresAt).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor(t / 1000);
  }, [data?.session?.expiresAt]);

  const [timeLeft, setTimeLeft] = useState({ minutes: 0, seconds: 0, totalSeconds: 0 });
  const [isExpiring, setIsExpiring] = useState(false);
  const [progress, setProgress] = useState(100);

  // Calculate time left from session expiry
  useEffect(() => {
    if (!expiresAtSeconds) return;

    const now = Math.floor(Date.now() / 1000);
    const maxDuration = Math.max(0, expiresAtSeconds - now) || 0;

    const calculate = () => {
      const current = Math.floor(Date.now() / 1000);
      const total = expiresAtSeconds - current;
      if (total <= 0) {
        onExpired?.();
        return { minutes: 0, seconds: 0, totalSeconds: 0 };
      }
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      setIsExpiring(minutes < 5);
      const pct = maxDuration > 0 ? Math.min(100, Math.max(0, (total / maxDuration) * 100)) : 0;
      setProgress(pct);
      return { minutes, seconds, totalSeconds: total };
    };

    setTimeLeft(calculate());
    const id = setInterval(() => setTimeLeft(calculate()), 1000);
    return () => clearInterval(id);
  }, [expiresAtSeconds, onExpired]);

  if (!expiresAtSeconds) return null;

  return (
    <div className={cx(container, className)} {...rest}>
      {/* Progress bar */}
      <div className={progressBar}>
        <div
          className={cx(progressBarFill, { [progressBarFillExpiring]: isExpiring })}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Timer badge */}
      <div className={timerWrapper}>
        <div className={cx(timer, { [timerExpiring]: isExpiring })}>
          <Clock size={16} />
          <span className={timerText}>
            {String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
          </span>
        </div>
      </div>
    </div>
  );
}

export default SessionTimer;
